<template>
  <div id="quickquote-content-step-1-component">
    <h-1-and-tooltip
      :h1="contentModel.SharedFields.LabelHeadingStep1"
      :tooltip="contentModel.SharedFields.TooltipHeadingStep1"
    />
    <section>
      <article class="flow-group">
        <div class="control">
          <h-3-and-tooltip
            :h3="contentModel.LabelSelectAddress"
            data-test="quickquote-content-headline-address"
            :tooltip="contentModel.TooltipSelectAddress"
          />
          <Field
            v-model="vueModel.TemplateModel.SelectedAddressTemplate"
            v-slot="{ field }"
            name="quickquote-validation-address"
            label="Adresse"
            :rules="'required'"
          >
            <address-search
              v-model="vueModel.TemplateModel.SelectedAddressTemplate"
              :name="field.name"
              data-test="quickquote-content-select-address"
              :init-value="vueModel.TemplateModel.SelectedAddressTemplate"
              :disabled="vueModel.IsKnownMember"
              placeholder="Indtast vejnavn"
            />
          </Field>
          <ValidationError name="quickquote-validation-address" />
          <p
            v-if="vueModel.TemplateModel.SelectedAddressTemplate"
            class="info-box is-open info-box-align-left"
            v-html="contentModel.LabelAddressInfoBox"
          />
          <h-3-and-tooltip
            :h3="contentModel.LabelPropertyType"
            :tooltip="contentModel.TooltipPropertyType"
            data-test="quickquote-content-headline-property-type"
          />

          <div
            v-for="(radio, index) in contentModel.MultilistPropertyTypes.DropdownElements"
            :key="index"
            class="radio-inline"
          >
            <Field
              :id="'PropertyType' + index"
              :modelValue="vueModel.TemplateModel.PropertyTypeEnum"
              @input="
                vueModel.TemplateModel.PropertyTypeEnum = $event.target.value;
                propertyTypeChange();
              "
              :rules="'custom_required'"
              type="radio"
              name="PropertyType"
              :value="radio.Value"
              label="Ejendomstype"
              :data-test="'quickquote-content-input-property-type-' + radio.Value"
            />
            <label :for="'PropertyType' + index" v-html="radio.Name" />
          </div>
          <ValidationError
            name="PropertyType"
            :overrides="{ custom_required: contentModel.LabelPropertyTypeValidationError }"
            data-test="quickquote-content-validation-property-type"
          />
        </div>
        <div class="control">
          <h-3-and-tooltip
            :h3="contentModel.LabelRoofType"
            :tooltip="contentModel.TooltipRoofType"
            data-test="quickquote-content-headline-roof-type"
          />
          <div
            v-for="(radio, index) in contentModel.MultilistRoofTypes.DropdownElements"
            :key="index"
            class="radio-inline"
          >
            <Field
              :id="'RoofType' + index"
              v-model="vueModel.TemplateModel.RoofTypeEnum"
              :rules="'custom_required'"
              type="radio"
              name="RoofType"
              :value="radio.Value"
              label="Tagtype"
              :data-test="'quickquote-content-input-roof-type-' + radio.Value"
            />
            <label :for="'RoofType' + index" v-html="radio.Name" />
          </div>
          <ValidationError
            name="RoofType"
            :overrides="{ custom_required: contentModel.LabelRoofTypeValidationError }"
            data-test="quickquote-content-validation-roof-type"
          />
        </div>

        <div class="control">
          <h-3-and-tooltip
            :h3="contentModel.LabelHasAlarm"
            :tooltip="contentModel.TooltipHasAlarm"
            data-test="quickquote-content-headline-has-alarm"
          />
          <div class="row">
            <div
              v-for="(radio, index) in contentModel.MultilistAlarmTypes.DropdownElements"
              :key="index"
              class="small-6 columns"
            >
              <div
                class="radio-stacked"
                :data-test="'quickquote-content-input-has-alarm-' + radio.Value"
              >
                <Field
                  :id="'HasAlarm-' + index"
                  v-model="vueModel.TemplateModel.HasAlarmEnum"
                  :rules="'custom_required'"
                  type="radio"
                  name="HasAlarm"
                  :value="radio.Value"
                  label="Tyverialarm"
                />
                <label :for="'HasAlarm-' + index" v-html="radio.Name" />
              </div>

              <div class="small-12 columns">
                <ValidationError
                  name="HasAlarm"
                  :overrides="{ custom_required: contentModel.LabelHasAlarmValidationError }"
                  data-test="quickquote-content-validation-has-alarm"
                />
              </div>
            </div>
          </div>
        </div>
        <hr />
        <div class="control">
          <h-3-and-tooltip
            :h3="contentModel.LabelAge"
            :tooltip="contentModel.TooltipAge"
            data-test="quickquote-content-headline-age"
          />
          <Field
            v-model="vueModel.TemplateModel.MemberAge"
            :rules="'required|numeric|min_value:18|max_value:110'"
            type="text"
            name="Age"
            label="Alder"
            :placeholder="contentModel.PlaceholderAge"
            :validateOnBlur="true"
            data-test="quickquote-content-input-age"
            :disabled="vueModel.IsKnownMember"
            @input="revalidate($event, this.form)"
          />
          <ValidationError
            name="Age"
            :overrides="{
              required: contentModel.LabelAgeRequiredValidationError,
              between: contentModel.LabelAgeDigitsValidationError,
              numeric: contentModel.LabelAgeNumericValidationError
            }"
            data-test="quickquote-content-validation-age"
          />
        </div>
        <hr />
        <div class="control">
          <questionnaire
            :options="policyQuestions"
            :always-show="true"
            :heading-three="true"
            @questionnaire-updated="getQuestionnaireAnswersFromId"
          />
        </div>
      </article>
    </section>
  </div>
</template>

<script>
import AddressSearch from '@/components/UI/AddressSearch.vue';
import Utility from '@/common/UtilityFunctions.js';
import QuickQuoteMixin from './QuickQuoteMixin.vue';
import H1AndTooltip from '@/components/UI/H1AndTooltip.vue';
import H3AndTooltip from '@/components/UI/H3AndTooltip.vue';
import ValidationError from '@/components/UI/ValidationError.vue';
import { FormContextKey } from 'vee-validate';
import { inject } from 'vue';

export default {
  name: 'ContentQuickQuote',
  components: {
    AddressSearch,
    H1AndTooltip,
    H3AndTooltip,
    ValidationError
  },
  setup() {
    return { form: inject(FormContextKey, undefined) };
  },
  emits: ['disable-next-button', 'update-personal-information'],
  mixins: [QuickQuoteMixin],
  props: {
    contentModel: {
      type: Object,
      default() {
        return {};
      }
    },
    vueModel: {
      type: Object,
      default() {
        return {};
      }
    },
    stepModel: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      isBlocked: false,
      blockedPropertyTypes: [1000003, 1000004],
      blockedRoofTypes: [1000003],
      tagManagerProps: {
        numberOfClaims: null,
        numberOfTheftClaims: null
      }
    };
  },
  computed: {
    personalInformationForTagManager() {
      return {
        event: 'track-event',
        eventCategory: 'indbo prisberegner',
        eventAction: '01 oplysninger submit ok',
        eventLabel: this.getHouseTypeFromId(this.vueModel.TemplateModel.PropertyTypeEnum),
        eventValue: '0',
        eventNoninteraction: true,
        data: {
          boligtype: this.getHouseTypeFromId(this.vueModel.TemplateModel.PropertyTypeEnum),
          tagtype: this.getRoofTypeFromId(this.vueModel.TemplateModel.RoofTypeEnum),
          'direkte alarm':
            this.vueModel.TemplateModel.HasAlarmEnum === 1000001
              ? 'ja'
              : this.vueModel.TemplateModel.HasAlarmEnum === 1000000
                ? 'nej'
                : '',
          alder: this.vueModel.TemplateModel.MemberAge,
          skader3år: this.tagManagerProps.numberOfClaims,
          tyveriskader3år: this.tagManagerProps.numberOfTheftClaims
        }
      };
    }
  },
  watch: {
    'vueModel.TemplateModel.RoofTypeEnum': function () {
      if (this.isBlockedRoofType()) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      } else {
        this.updateBlockStatus();
      }
    },
    'vueModel.TemplateModel.SelectedAddressTemplate.tekst': function () {
      Utility.pushToTagManager({
        event: 'track-ga-vp',
        virtualPath: '/virtual/engagement/indbo-prisberegner-01-adressefelt-forladt',
        virtualTitle: 'Virtuel - Beregn pris - Indbo - adressefelt aktivitet'
      });
    },
    personalInformationForTagManager() {
      this.$emit('update-personal-information', this.personalInformationForTagManager);
    }
  },
  mounted() {},
  methods: {
    propertyTypeChange: function () {
      if (this.isBlockedPropertyType()) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      } else {
        this.updateBlockStatus();
      }
    },
    isBlockedPropertyType() {
      return this.blockedPropertyTypes.includes(
        parseInt(this.vueModel.TemplateModel.PropertyTypeEnum)
      );
    },
    isBlockedRoofType() {
      return this.blockedRoofTypes.includes(parseInt(this.vueModel.TemplateModel.RoofTypeEnum));
    },
    // For Tag Manager tracking:
    getHouseTypeFromId(id) {
      let houseType;
      switch (id) {
        case 1000001:
          houseType = 'hus/rækkehus';
          break;
        case 1000002:
          houseType = 'lejlighed';
          break;
        case 1000003:
          houseType = 'husbåd';
          break;
        case 1000004:
          houseType = 'ferielejlighed';
          break;
        default:
          houseType = '';
      }
      return houseType;
    },
    getRoofTypeFromId(id) {
      let houseType;
      switch (id) {
        case 1000001:
          houseType = 'hårdt tag';
          break;
        case 1000002:
          houseType = 'blødt tag';
          break;
        case 1000003:
          houseType = 'blødt tag med brandhæmmende foranstaltninger';
          break;
        default:
          houseType = '';
      }
      return houseType;
    },
    updateBlockStatus() {
      if (!this.isBlocked && !this.isBlockedPropertyType() && !this.isBlockedRoofType()) {
        this.$emit('disable-next-button', false);
      }
    },
    getQuestionnaireAnswersFromId(param) {
      switch (param.questionId) {
        case 1001068:
          this.tagManagerProps.numberOfClaims = param.answer.Value;

          break;
        case 1001069:
          this.tagManagerProps.numberOfTheftClaims = param.answer.Value;
          break;
      }

      if (param.answer.Block) {
        this.$emit('disable-next-button', true);
        this.showErrorModal();
      }
      this.isBlocked = param.blockStatus;
      this.updateBlockStatus();
    }
  },
  template: '#templateForContentQuickQuote'
};
</script>
